/**
 * ч.1095: попередження «контрагент з таким кодом уже є» — над полем
 * ЄДРПОУ / РНОКПП у формах створення контрагента.
 *
 * ч.1237 (знахідка власника: «при створенні контрагента з виписки не перевіряє, що такий уже
 * є»): шукаємо не лише за кодом, а й за банківським рахунком і назвою — у виписці коду часто
 * немає, а в наявній картці він може бути не заповнений.
 *
 * Не блокує збереження: однаковий код буває у філій та окремих отримувачів.
 * Але найчастіше це дубль — тому одразу пропонуємо відкрити наявну картку
 * (або, у швидкому створенні з документа, обрати наявного контрагента).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { contractors, type ContractorSimilarMatch } from '@/api/catalogs';

/** Збіг, картку якого користувач бачить (ч.1312: прихований збіг кнопок не має). */
type VisibleMatch = ContractorSimilarMatch & { id: string };

/** Код повний — є сенс шукати (ЄДРПОУ 8, РНОКПП 10, ІПН 12 цифр). */
export function isCompleteCode(code: string | null | undefined): boolean {
  return /^\d{8}$|^\d{10}$|^\d{12}$/.test((code ?? '').trim());
}

const REASON_LABEL: Record<string, string> = {
  code: 'той самий код',
  iban: 'той самий рахунок',
  name: 'така сама назва',
};

/** Значення, що «заспокоїлось»: поки користувач набирає назву, запитів не шлемо. */
function useSettled(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function ContractorCodeHint({ code, name, iban, excludeId, onOpen, onPick, onMatches }: {
  code: string | null | undefined;
  /** Назва, яку вводять, — шукаємо й за нею (без оргформи, лапок і регістру). */
  name?: string | null;
  /** Рахунок контрагента (з виписки) — шукаємо серед рахунків наявних контрагентів. */
  iban?: string | null;
  /** Картка, яку редагуємо, — її саму дублем не вважаємо. */
  excludeId?: string | null;
  /** «відкрити картку» наявного контрагента. */
  onOpen: (m: VisibleMatch) => void;
  /** Швидке створення з документа: «обрати його» замість створення дубля. */
  onPick?: (m: VisibleMatch) => void;
  /** Скільки схожих знайдено — форма може перейменувати кнопку створення. */
  onMatches?: (count: number) => void;
}) {
  const value = (code ?? '').trim();
  const byCode = isCompleteCode(value) ? value : '';
  const byIban = (iban ?? '').replace(/\s+/g, '').toUpperCase();
  const byName = useSettled((name ?? '').trim(), 400);
  const enabled = !!byCode || !!byIban || byName.length >= 3;
  const q = useQuery({
    queryKey: ['contractor-similar', byCode, byIban, byName, excludeId ?? null],
    queryFn: () => contractors.similar({ code: byCode, iban: byIban, name: byName, excludeId }),
    enabled,
    staleTime: 30_000,
  });
  const matches = enabled ? (q.data ?? []) : [];
  const count = matches.length;
  useEffect(() => { onMatches?.(count); }, [count]); // eslint-disable-line react-hooks/exhaustive-deps
  if (count === 0) return null;
  const sameCode = !!byCode && matches.some((m) => m.reasons.includes('code'));
  return (
    <div role="alert" style={{
      background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)',
      color: 'var(--badge-amber-fg)', borderRadius: 6, padding: '0.5rem 0.75rem',
      margin: '0 0 0.5rem', fontSize: '0.85rem',
    }}>
      <b>{sameCode ? `Контрагент з кодом ${byCode} уже є в довіднику.` : 'Схожий контрагент уже є в довіднику.'}</b>{' '}
      Якщо це той самий — не створюйте дубль; якщо філія чи окремий отримувач — можна зберегти.
      <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.1rem' }}>
        {matches.map((m) => m.hidden ? (
          // ч.1312: роль показує лише власних контрагентів — чужого з тим самим кодом не видно.
          <li key="hidden" data-testid="contractor-hint-hidden" style={{ marginBottom: 2 }}>
            з цим кодом уже є контрагент, закріплений за іншим користувачем. Щоб не створювати
            дубль, попросіть адміністратора акаунта передати його вам.
          </li>
        ) : (
          <li key={m.id} style={{ marginBottom: 2 }}>
            {m.name}
            {m.full_name && m.full_name !== m.name ? ` (${m.full_name})` : ''}
            {m.code ? `, код ${m.code}` : ''}
            {' — '}{m.reasons.map((r) => REASON_LABEL[r] ?? r).join(', ')}{' '}
            {onPick && (
              <button type="button" className="primary" style={{ marginLeft: 6 }}
                onClick={() => onPick(m as VisibleMatch)}>обрати його</button>
            )}
            <button type="button" style={{ marginLeft: 6 }} onClick={() => onOpen(m as VisibleMatch)}>
              відкрити картку
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
